import type {
	SceneFactProposal,
	SceneFactProposalAction,
	SceneFactProposalRetest,
	VerificationVideo,
} from "@sitescene/types";

import {
	SceneFactProposalStatus,
	SceneFactRetestArmStatus,
} from "@sitescene/types";
import {
	type QueryClient,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";

import { apiClient, ApiError } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";
import { useOrgSelection } from "@/context/use-org-selection";

/*
 * Response shapes of the scene-facts routes. They mirror the
 * `SceneFactProposalService` result types in @sitescene/services, which the
 * browser cannot import; dates arrive as ISO strings.
 */

export interface CreateSceneProposalInput {
	/** Exactly one of `eventId` and `verificationId`. */
	eventId?: string;
	note: string;
	verificationId?: string;
}

/** What Stage 1 claimed about the clip the proposal is about. */
export interface SceneFactClaim {
	description?: string;
	labels: string[];
	measurementId: string;
	mediaEndTime?: string;
	mediaStartTime?: string;
	severity?: number;
	type: string;
}

export interface SceneFactEventSummary {
	description: string;
	id: string;
	severity: number;
	startTimestamp: Date | string;
	status: string;
	title: string;
}

export interface SceneFactProposalDetail {
	claim: null | SceneFactClaim;
	event: null | SceneFactEventSummary;
	/** Every revision applied to this camera, most recently applied first. */
	history: SceneFactProposal[];
	/** Carries its latest re-test, when one was started, as `retest`. */
	proposal: SceneFactProposal;
	sensor: null | SceneFactSensorSummary;
	/** Pending, and the camera's facts moved on since it was drafted. */
	stale: boolean;
	/**
	 * Drafting for longer than any live draft can take: its worker died, and
	 * Redraft is allowed. The server decides, so the page needs no clock.
	 */
	stuck: boolean;
}

export interface SceneFactProposalListItem extends SceneFactProposal {
	eventTitle?: string;
	sensorName?: string;
}

/** The camera's scene reference as it stands, in its three labelled parts. */
export interface SceneFactSensorSummary {
	/** The applied revision behind `sceneFacts`; only it can be reverted. */
	currentProposalId?: string;
	id: string;
	name: string;
	/** The description the analyzer sees — absent until it is final. */
	sceneDescription?: string;
	sceneFacts?: string;
	sceneFactsUpdatedAt?: Date | string;
	sceneGeometry?: string;
}

export interface SceneProposalActionInput {
	action: SceneFactProposalAction;
	/** Apply only: staff-edited facts, guarded again server-side. */
	facts?: string;
	proposalId: string;
	/** Reject only. */
	reason?: string;
}

const DRAFT_POLL_MS = 3000;
/** A draft past the fast window is checked less often, until it is stuck. */
const DRAFT_SLOW_POLL_MS = 30_000;
const RETEST_POLL_MS = 5000;
/**
 * How long to poll a draft quickly. Past this it is polled slowly rather than
 * dropped, so the page keeps its promise to refresh, until the server calls
 * it stuck and Redraft takes over.
 */
const DRAFT_POLL_LIMIT_MS = 10 * 60 * 1000;
/**
 * How long to poll a re-test before calling it overdue. The eval worker
 * reports a failure, but a message lost on the way would otherwise keep the
 * page polling for as long as it stays open; past this the page says so and
 * offers a manual check instead.
 */
const RETEST_POLL_LIMIT_MS = 15 * 60 * 1000;

const within = (since: Date | string, limitMs: number) =>
	Date.now() - new Date(since).getTime() < limitMs;

/** Both arms have landed, one way or the other. */
export const isRetestSettled = (retest: SceneFactProposalRetest): boolean =>
	retest.arms.every((arm) => arm.status !== SceneFactRetestArmStatus.Pending);

/**
 * Still unsettled long after both arms should have landed, as of `checkedAt`
 * (epoch ms). Pass the query's `dataUpdatedAt`, so the answer changes only
 * when the data does and the page and the poll agree on it.
 */
export const isRetestOverdue = (
	retest: SceneFactProposalRetest,
	checkedAt: number,
): boolean =>
	!isRetestSettled(retest) &&
	checkedAt - new Date(retest.requestedAt).getTime() >= RETEST_POLL_LIMIT_MS;

/**
 * A 409 from the scene-facts routes: the proposal or the camera moved on
 * since the page loaded — most often the camera's facts changed after the
 * draft ran, and Redraft is the way forward.
 */
export const isConflictError = (error: unknown): boolean =>
	error instanceof ApiError && error.statusCode === 409;

/**
 * The API's own message when it sent one. The client swaps a 5xx message for
 * a generic one, but the body still says what went wrong — a re-test that
 * could not be queued, say.
 */
export const apiErrorMessage = (error: Error): string => {
	if (error instanceof ApiError) {
		const data = error.data as undefined | { message?: unknown };
		if (typeof data?.message === "string" && data.message) {
			return data.message;
		}
	}
	return error.message;
};

const useOrgCode = () => {
	const { selected } = useOrgSelection();
	return selected?.orgCode ?? null;
};

/**
 * Refresh every proposal list and detail of the organization. Not the clip
 * URLs: re-signing one would reload the player mid-playback.
 */
const invalidateProposals = (queryClient: QueryClient, orgCode: string) =>
	Promise.all([
		queryClient.invalidateQueries({
			queryKey: ["shadow", "scene-facts", "proposals", orgCode],
		}),
		queryClient.invalidateQueries({
			queryKey: ["shadow", "scene-facts", "proposal", orgCode],
		}),
	]);

/**
 * The review queue of the selected organization, newest first. `statuses`
 * rides as `status[]` (comma-joined), the API's array convention; omitted for
 * every status.
 */
export const useSceneProposals = (
	statuses: readonly SceneFactProposalStatus[] | undefined,
) => {
	const orgCode = useOrgCode();
	const status = statuses?.join(",") ?? "";

	return useQuery({
		enabled: orgCode !== null,
		queryFn: async () => {
			const response = await apiClient.get<SceneFactProposalListItem[]>(
				ENDPOINTS.shadow.sceneFacts.proposals,
				status ? { params: { "status[]": status } } : undefined,
			);
			return response.data;
		},
		queryKey: queryKeys.sceneFacts.proposals(orgCode ?? "", status),
	});
};

/**
 * One proposal with everything its review page shows, its latest re-test
 * included. Both a draft and a re-test finish in a worker — a Gemini video
 * call can outlast API Gateway — so the page polls: a draft until the status
 * moves on or the server calls it stuck, a re-test until both arms land or it
 * is overdue.
 */
export const useSceneProposal = (proposalId: string | undefined) => {
	const orgCode = useOrgCode();

	return useQuery({
		enabled: orgCode !== null && Boolean(proposalId),
		queryFn: async () => {
			const response = await apiClient.get<null | SceneFactProposalDetail>(
				ENDPOINTS.shadow.sceneFacts.proposal(proposalId ?? ""),
			);
			return response.data;
		},
		queryKey: queryKeys.sceneFacts.proposal(orgCode ?? "", proposalId ?? ""),
		refetchInterval: (query) => {
			const detail = query.state.data;
			if (!detail) {
				return false;
			}
			if (detail.proposal.status === SceneFactProposalStatus.Drafting) {
				if (detail.stuck) {
					return false;
				}
				return within(detail.proposal.updatedAt, DRAFT_POLL_LIMIT_MS)
					? DRAFT_POLL_MS
					: DRAFT_SLOW_POLL_MS;
			}
			// Judged as of the last fetch, as the page judges it, so polling stops
			// exactly when the page starts calling the re-test overdue.
			const { retest } = detail.proposal;
			return retest &&
				!isRetestSettled(retest) &&
				!isRetestOverdue(retest, query.state.dataUpdatedAt)
				? RETEST_POLL_MS
				: false;
		},
	});
};

/**
 * A signed playback URL for the proposal's clip, held for most of its hour
 * like the verification clip.
 */
export const useSceneProposalVideo = (proposalId: string | undefined) => {
	const orgCode = useOrgCode();

	return useQuery({
		enabled: orgCode !== null && Boolean(proposalId),
		gcTime: 0,
		queryFn: async () => {
			const response = await apiClient.get<VerificationVideo>(
				ENDPOINTS.shadow.sceneFacts.proposalVideo(proposalId ?? ""),
			);
			return response.data;
		},
		queryKey: queryKeys.sceneFacts.proposalVideo(
			orgCode ?? "",
			proposalId ?? "",
		),
		retry: false,
		staleTime: 50 * 60 * 1000,
	});
};

/**
 * Queue a draft from a staff note about one clip, named by verification or
 * by event. Resolves with the new `drafting` proposal — or a `failed` one
 * when the draft could not be queued — for the caller to open.
 */
export const useCreateSceneProposal = () => {
	const queryClient = useQueryClient();
	const orgCode = useOrgCode() ?? "";

	return useMutation({
		mutationFn: async (input: CreateSceneProposalInput) => {
			const response = await apiClient.post<SceneFactProposal>(
				ENDPOINTS.shadow.sceneFacts.proposals,
				input,
			);
			return response.data;
		},
		onSuccess: async () => {
			await invalidateProposals(queryClient, orgCode);
		},
	});
};

/**
 * Apply, reject, redraft or revert one proposal. Takes the proposal id per
 * call because Revert acts on a revision in the camera's history, not only on
 * the proposal the page is showing.
 */
export const useSceneProposalAction = () => {
	const queryClient = useQueryClient();
	const orgCode = useOrgCode() ?? "";

	return useMutation({
		mutationFn: async ({
			action,
			proposalId,
			...body
		}: SceneProposalActionInput) => {
			const response = await apiClient.post<SceneFactProposal>(
				ENDPOINTS.shadow.sceneFacts.proposalAction(proposalId, action),
				body,
			);
			return response.data;
		},
		onError: async (error) => {
			// The page is behind the server. Refresh it so the stale warning, or
			// the status someone else moved it to, shows beside the message.
			if (isConflictError(error)) {
				await invalidateProposals(queryClient, orgCode);
			}
		},
		onSuccess: async () => {
			await invalidateProposals(queryClient, orgCode);
		},
	});
};

/**
 * Run a pending proposal through the real first-pass worker, current against
 * proposed. The API answers with the proposal detail carrying the new
 * re-test, both arms pending; it goes straight into the detail cache, so the
 * cards appear at once and the detail's poll picks up from there.
 */
export const useStartSceneFactRetest = (proposalId: string) => {
	const queryClient = useQueryClient();
	const orgCode = useOrgCode() ?? "";

	return useMutation({
		mutationFn: async (input: { facts?: string }) => {
			const response = await apiClient.post<SceneFactProposalDetail>(
				ENDPOINTS.shadow.sceneFacts.proposalRetest(proposalId),
				input,
			);
			return response.data;
		},
		onError: async () => {
			// The server may have moved on anyway: jobs that could not be sent
			// leave both arms written as failed, and a conflict means the proposal
			// is no longer pending. Refresh so the page shows where it stands.
			await queryClient.invalidateQueries({
				queryKey: queryKeys.sceneFacts.proposal(orgCode, proposalId),
			});
		},
		onSuccess: async (detail) => {
			queryClient.setQueryData(
				queryKeys.sceneFacts.proposal(orgCode, proposalId),
				detail,
			);
			// The queue's rows carry the re-test too; refreshed when next shown.
			await queryClient.invalidateQueries({
				queryKey: ["shadow", "scene-facts", "proposals", orgCode],
			});
		},
	});
};
